export const EXPLORER_STORAGE_KEY = 'tgc:data-explorer:query:v1';

export const EMPTY_DATE_FILTER = {
  preset: 'all',
  dateField: '',
  dateFrom: '',
  dateTo: ''
};

const asArray = (v) => (Array.isArray(v) ? v : []);
const asObject = (v) =>
  v && typeof v === 'object' && !Array.isArray(v) ? v : {};

export function hasMeaningfulQuery(state) {
  if (!state) return false;
  const table = asObject(state.table);
  const dateFilter = asObject(state.dateFilter);
  return Boolean(
    state.view ||
      (state.canvasPrimary && state.canvasPrimary !== 'User') ||
      asArray(state.columns).length ||
      asArray(state.relatedSections).length ||
      asArray(state.filters).length ||
      dateFilter.dateField ||
      dateFilter.dateFrom ||
      dateFilter.dateTo ||
      (dateFilter.preset && dateFilter.preset !== 'all') ||
      state.search ||
      asObject(state.startFrom).templateKey ||
      asObject(state.startFrom).configId ||
      asArray(table.sortModel).length ||
      asArray(asObject(table.filterModel).items).length ||
      Object.keys(asObject(table.columnVisibilityModel)).length ||
      Object.keys(asObject(table.pinnedColumns)).length ||
      table.pivotActive ||
      table.groupByField
  );
}

// Reduce live page state to the durable snapshot, stamped with its owner.
export function serializeExplorerState(state, owner) {
  if (!state) return null;
  return {
    v: 1,
    owner: owner == null ? null : String(owner),
    view: state.view || null,
    canvasPrimary: state.canvasPrimary || 'User',
    columns: asArray(state.columns),
    relatedSections: asArray(state.relatedSections).map((s) => ({
      model: s.model,
      path: asArray(s.path)
    })),
    filters: asArray(state.filters),
    dateFilter: { ...EMPTY_DATE_FILTER, ...asObject(state.dateFilter) },
    search: state.search || '',
    table: asObject(state.table),
    startFrom: {
      templateKey: asObject(state.startFrom).templateKey || '',
      configId: asObject(state.startFrom).configId || ''
    }
  };
}

export function deserializeExplorerState(raw, owner) {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.v !== 1) return null;
  // No owner on either side is a refusal, not a match — we cannot show the
  // snapshot was this user's.
  if (!owner || !raw.owner || String(raw.owner) !== String(owner)) return null;

  const restored = {
    view: raw.view && typeof raw.view === 'object' ? raw.view : null,
    canvasPrimary:
      typeof raw.canvasPrimary === 'string' && raw.canvasPrimary
        ? raw.canvasPrimary
        : 'User',
    columns: asArray(raw.columns).filter((c) => typeof c === 'string'),
    relatedSections: asArray(raw.relatedSections)
      .filter((s) => s && typeof s.model === 'string')
      .map((s) => ({ model: s.model, path: asArray(s.path) })),
    filters: asArray(raw.filters).filter((f) => f && typeof f === 'object'),
    dateFilter: { ...EMPTY_DATE_FILTER, ...asObject(raw.dateFilter) },
    search: typeof raw.search === 'string' ? raw.search : '',
    table: asObject(raw.table),
    startFrom: {
      templateKey: asObject(raw.startFrom).templateKey || '',
      configId: asObject(raw.startFrom).configId || ''
    }
  };

  // A snapshot that restores to the untouched page is not worth acting on —
  // returning null keeps the caller's "did we restore anything?" check honest,
  // so it does not fire a query for an empty canvas.
  return hasMeaningfulQuery(restored) ? restored : null;
}

function safeSessionStorage() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function loadExplorerState(owner, storage = safeSessionStorage()) {
  if (!storage) return null;
  try {
    const raw = storage.getItem(EXPLORER_STORAGE_KEY);
    if (!raw) return null;
    return deserializeExplorerState(JSON.parse(raw), owner);
  } catch {
    return null;
  }
}

export function saveExplorerState(
  state,
  owner,
  storage = safeSessionStorage()
) {
  if (!storage) return;
  // No owner: write nothing rather than a snapshot that could never be
  // restored. Deliberately not a clear either — a transient null user must not
  // destroy a good snapshot. Logout clears it explicitly.
  if (!owner) return;
  try {
    if (!hasMeaningfulQuery(state)) {
      storage.removeItem(EXPLORER_STORAGE_KEY);
      return;
    }
    storage.setItem(
      EXPLORER_STORAGE_KEY,
      JSON.stringify(serializeExplorerState(state, owner))
    );
  } catch {
    /* quota / disabled storage — persistence is best-effort */
  }
}

// Drop the stored query. Called by RESET (the user asked for a clean slate) and
// on logout, so the next user in this browser tab starts clean and no snapshot
// naming a signed-out user sits in storage waiting to be offered back.
export function clearExplorerState(storage = safeSessionStorage()) {
  if (!storage) return;
  try {
    storage.removeItem(EXPLORER_STORAGE_KEY);
  } catch {
    /* disabled storage — best-effort */
  }
}
